import {
  BlockDiagramViewSettings,
  ModernizedBlockDiagramViewSettings,
} from '../../../types';
import {
  CompositeLayoutData,
  GenericLayoutData,
  GraphComponent,
  HierarchicalLayout,
  HierarchicalLayoutConfig,
  HierarchicalLayoutData,
  HierarchicalLayoutEdgeDescriptor,
  HierarchicalLayoutRoutingStyle,
  IEdge,
  ILabel,
  Insets,
  LayoutDescriptor,
  LayoutOrientation,
  PortAdjustmentPolicy,
  EdgeLabelPreferredPlacement,
  TimeSpan,
} from '@ardoq/yfiles';
import {
  GraphEdgeBase,
  Rect,
  ViewpointRelationshipDiagramViewModel,
  inside,
  isCollapsibleGroup,
  type YGraphState,
} from '@ardoq/graph';
import { processDuplicateLabels } from '@ardoq/graph/yfiles';
import { WILDCARD } from '@ardoq/common-helpers';
import type { ArrowType } from '@ardoq/api-types';
import { getCriticalPaths } from '../../criticalPaths';
import {
  getNewIdsTargetedByNewIds,
  incrementalEdgeHint,
  incrementalNodeHint,
  toEdgeEndpoints,
} from '../../incrementalLayoutHints';
import { isContextNode } from 'yfilesExtensions/styles/nodeDecorator';
import { markerLength } from './graphBuilder/canvasEdge/markerMetrics';
import { maximumGraphLayoutDuration } from 'tabview/relationshipDiagrams/maximumGraphLayoutDuration';
import { UrlLabelStyle } from 'yfilesExtensions/styles/UrlLabelStyle';
import { OpenGroupOtherLabelStyle } from './groupLabels/OpenGroupOtherLabelStyle';
import { GroupSubLabelStyle } from './groupLabels/GroupSubLabelStyle';
import { INVISIBLE_SEPARATOR } from './consts';
import {
  HAS_REPRESENTATION_DATA,
  HAS_URL_FIELDS,
  MAIN_LABEL,
  OTHER_LABELS,
  SUB_LABEL,
} from 'tabview/graphComponent/layoutStage/consts';
import { safeGetRepresentationDataFromINodeTag } from './modernizedBlockDiagramRepresentationData';

const ANIMATION_DURATION = 500;

const MINIMUM_LAYER_DISTANCE = 35;
const MINIMUM_SEGMENT_LENGTH = 16;
const MARKER_CLEARANCE = 4;

const standardHierarchicLayoutOptions: HierarchicalLayoutConfig = {
  nodeLabelPlacement: 'consider',
  edgeLabelPlacement: 'integrated',
  groupLayeringPolicy: 'recursive',
  fromScratchLayeringStrategy: 'hierarchical-optimal',
};

const layoutOrientationString = (layoutOrientation: LayoutOrientation) => {
  switch (layoutOrientation) {
    case LayoutOrientation.TOP_TO_BOTTOM:
      return 'top-to-bottom';
    case LayoutOrientation.BOTTOM_TO_TOP:
      return 'bottom-to-top';
    case LayoutOrientation.LEFT_TO_RIGHT:
      return 'left-to-right';
    case LayoutOrientation.RIGHT_TO_LEFT:
      return 'right-to-left';
  }
};
const ROUTING_STYLE_DESCRIPTOR = {
  defaultRoutingStyle: HierarchicalLayoutRoutingStyle.ORTHOGONAL,
};

const segmentLengthFor = (arrowType: ArrowType): number =>
  Math.max(MINIMUM_SEGMENT_LENGTH, markerLength(arrowType) + MARKER_CLEARANCE);

const toEdgeDescriptor = (
  lineBeginning: ArrowType,
  lineEnding: ArrowType
): HierarchicalLayoutEdgeDescriptor => {
  const minimumFirstSegmentLength = segmentLengthFor(lineBeginning);
  const minimumLastSegmentLength = segmentLengthFor(lineEnding);
  return new HierarchicalLayoutEdgeDescriptor({
    backLoopRouting: false,
    routingStyleDescriptor: ROUTING_STYLE_DESCRIPTOR,
    minimumFirstSegmentLength,
    minimumLastSegmentLength,
    minimumLength: minimumFirstSegmentLength + minimumLastSegmentLength,
  });
};

const DEFAULT_EDGE_DESCRIPTOR = new HierarchicalLayoutEdgeDescriptor({
  backLoopRouting: false,
  routingStyleDescriptor: ROUTING_STYLE_DESCRIPTOR,
  minimumFirstSegmentLength: MINIMUM_SEGMENT_LENGTH,
  minimumLastSegmentLength: MINIMUM_SEGMENT_LENGTH,
  minimumLength: 2 * MINIMUM_SEGMENT_LENGTH,
});

const edgeDescriptors = new Map<string, HierarchicalLayoutEdgeDescriptor>();

const getEdgeDescriptor = ({
  tag,
}: IEdge): HierarchicalLayoutEdgeDescriptor => {
  if (!(tag instanceof GraphEdgeBase)) {
    return DEFAULT_EDGE_DESCRIPTOR;
  }
  const lineBeginning = tag.getLineBeginning();
  const lineEnding = tag.getLineEnding();
  const key = `${lineBeginning}|${lineEnding}`;
  const cached = edgeDescriptors.get(key);
  if (cached) {
    return cached;
  }
  const descriptor = toEdgeDescriptor(lineBeginning, lineEnding);
  edgeDescriptors.set(key, descriptor);
  return descriptor;
};

const longestMarkerLength = ({ tag }: IEdge): number =>
  tag instanceof GraphEdgeBase
    ? Math.max(
        markerLength(tag.getLineBeginning()),
        markerLength(tag.getLineEnding())
      )
    : 0;

/**
 * Integrated labeling puts the widest label of a layer half the layer distance
 * from the node it points at, so a labeled edge needs half the distance to
 * hold its marker and some clearance.
 */
const toMinimumLayerDistance = (edges: Iterable<IEdge>): number =>
  [...edges]
    .filter(({ labels }) => labels.size > 0)
    .reduce(
      (distance, edge) =>
        Math.max(distance, 2 * (longestMarkerLength(edge) + MARKER_CLEARANCE)),
      MINIMUM_LAYER_DISTANCE
    );

type GetLayoutInfoArgs = {
  nodeCount: number;
  edgeCount: number;
  layoutOrientation: LayoutOrientation;
  minimumLayerDistance: number;
};

const getLayoutInfo = ({
  nodeCount,
  edgeCount,
  layoutOrientation,
  minimumLayerDistance,
}: GetLayoutInfoArgs) => {
  const layoutConfig: HierarchicalLayoutConfig = {
    ...standardHierarchicLayoutOptions,
    minimumLayerDistance,
    stopDuration: TimeSpan.fromMilliseconds(
      maximumGraphLayoutDuration(nodeCount, edgeCount)
    ),
    layoutOrientation: layoutOrientationString(layoutOrientation),
    defaultEdgeDescriptor: DEFAULT_EDGE_DESCRIPTOR,
  };
  const layoutDescriptor: LayoutDescriptor = {
    name: 'HierarchicalLayout',
    properties: layoutConfig,
  };

  return layoutDescriptor;
};

const DEFAULT_PLACEMENT_DESCRIPTOR = new EdgeLabelPreferredPlacement();

const configureLayout = (
  graphComponent: GraphComponent,
  {
    nodes,
    groups,
    edges,
    expandedStateChangingGroupId,
  }: Pick<
    ViewpointRelationshipDiagramViewModel<ModernizedBlockDiagramViewSettings>,
    'nodes' | 'groups' | 'edges' | 'expandedStateChangingGroupId'
  >,
  {
    layoutOrientation,
    separateReferences,
  }: Pick<BlockDiagramViewSettings, 'layoutOrientation' | 'separateReferences'>
) => {
  const nodeCount = nodes.add.length + nodes.update.length;
  const edgeCount = edges.add.length + edges.update.length;

  const layoutDescriptor = getLayoutInfo({
    nodeCount,
    edgeCount,
    layoutOrientation,
    minimumLayerDistance: toMinimumLayerDistance(graphComponent.graph.edges),
  });

  const isUpdate =
    nodes.update.length + groups.update.length + edges.update.length > 0;

  // when expanding or collapsing a group, avoid a distracting re-layout-- the graph layout should be stable, so the user's eye can follow the group as it grows or shrinks.
  // for other updates, especially toggling "separate references," a from-scratch layout is preferred, so that edge crossings can be re-evaluated given the newly grouped or ungrouped edges.
  layoutDescriptor.properties!.fromSketchMode = Boolean(
    expandedStateChangingGroupId
  );

  const { graph, contentBounds, viewport } = graphComponent;

  const customMappingLayoutData = new GenericLayoutData();

  customMappingLayoutData.addItemMapping(
    MAIN_LABEL,
    ({ labels }) => labels.at(0)?.text
  );
  customMappingLayoutData.addItemMapping(
    SUB_LABEL,
    ({ labels }) =>
      labels.find(({ style }) => style === GroupSubLabelStyle.Instance)?.text ||
      INVISIBLE_SEPARATOR
  );
  customMappingLayoutData.addItemMapping(HAS_URL_FIELDS, ({ labels }) =>
    Boolean(labels.find(({ style }) => style === UrlLabelStyle.Instance))
  );
  customMappingLayoutData.addItemMapping(HAS_REPRESENTATION_DATA, ({ tag }) =>
    Boolean(safeGetRepresentationDataFromINodeTag(tag))
  );
  customMappingLayoutData.addItemMapping(
    OTHER_LABELS,
    ({ labels }) =>
      labels
        .filter(({ style }) => style instanceof OpenGroupOtherLabelStyle)
        .map(({ text }) => text)
        .toArray()
        .join(INVISIBLE_SEPARATOR) || INVISIBLE_SEPARATOR
  );

  let layoutData: HierarchicalLayoutData;

  if (!separateReferences && graph.tag) {
    const state = graph.tag as YGraphState;
    const {
      edgeSourceGroup,
      edgeSourceGroups,
      edgeTargetGroup,
      edgeTargetGroups,
    } = state;

    layoutData = new HierarchicalLayoutData({
      sourceGroupIds: edgeSourceGroup,
      targetGroupIds: edgeTargetGroup,
      edgeCrossingCosts: (edge: IEdge) => {
        const sourceGroupId = edgeSourceGroup(edge) || '';
        const targetGroupId = edgeTargetGroup(edge) || '';

        const sourceGroupEdgesCount =
          edgeSourceGroups.get(sourceGroupId)?.length ?? 0;
        const targetGroupEdgesCount =
          edgeTargetGroups.get(targetGroupId)?.length ?? 0;

        return Math.max(sourceGroupEdgesCount + targetGroupEdgesCount, 1);
      },
    });

    const edgeLabelPreferredPlacement = processDuplicateLabels(graph);
    layoutData.edgeLabelPreferredPlacements.mapperFunction = (label: ILabel) =>
      edgeLabelPreferredPlacement.get(label) ?? DEFAULT_PLACEMENT_DESCRIPTOR;
  } else {
    layoutData = new HierarchicalLayoutData();
  }
  layoutData.edgeDescriptors = getEdgeDescriptor;
  const expandingOrCollapsingAll = expandedStateChangingGroupId === WILDCARD;
  if (expandingOrCollapsingAll) {
    graph.nodes.forEach(node => {
      if (graph.isGroupNode(node)) {
        graph.adjustGroupNodeLayout(node);
      }
    });
  } else {
    const expandedStateChangingNode =
      expandedStateChangingGroupId &&
      graph.nodes.find(
        node =>
          isCollapsibleGroup(node.tag) &&
          node.tag.id === expandedStateChangingGroupId
      );
    if (expandedStateChangingNode) {
      graph.adjustGroupNodeLayout(expandedStateChangingNode);
      graph.groupingSupport
        .getDescendants(expandedStateChangingNode)
        .filter(node => graph.isGroupNode(node))
        .forEach(groupNode => graph.adjustGroupNodeLayout(groupNode));
    }
  }

  const wasFullGraphDisplayed = inside(
    Rect.fromRectLike(contentBounds),
    Rect.fromRectLike(viewport)
  );
  const animateViewport =
    expandedStateChangingGroupId !== null &&
    (wasFullGraphDisplayed || expandingOrCollapsingAll);

  const newIds = new Set([...nodes.add, ...groups.add, ...edges.add]);
  const isTogglingOneGroup =
    expandedStateChangingGroupId !== null && !expandingOrCollapsingAll;
  const incrementallyPlacedNodeIds = isTogglingOneGroup
    ? getNewIdsTargetedByNewIds(
        newIds,
        graph.edges.map(toEdgeEndpoints).toArray()
      )
    : newIds;
  customMappingLayoutData.addItemMapping(
    HierarchicalLayout.INCREMENTAL_NODE_HINTS_DATA_KEY,
    incrementalNodeHint(incrementallyPlacedNodeIds)
  );
  customMappingLayoutData.addItemMapping(
    HierarchicalLayout.INCREMENTAL_EDGE_HINTS_DATA_KEY,
    incrementalEdgeHint(newIds)
  );
  const criticalPaths = getCriticalPaths({
    graph,
    contextNode: graph.nodes.find(isContextNode),
  });
  if (criticalPaths.size) {
    layoutData.criticalEdgePriorities = edge => criticalPaths.get(edge) ?? null;
  }

  return {
    layoutDescriptor,
    layoutData: new CompositeLayoutData(layoutData, customMappingLayoutData),
    duration: isUpdate
      ? TimeSpan.fromMilliseconds(ANIMATION_DURATION)
      : TimeSpan.ZERO,
    animateViewport,
    updateContentRect: true,
    targetBoundsInsets: Insets.EMPTY,
    portAdjustmentPolicy: PortAdjustmentPolicy.ALWAYS, // shorten or elongate edges according to the outline as defined by ArdoqNodeStyle/IsometricNodeStyle. this prevents the edges from appearing "on top of" the faces of the isometric blocks.
  };
};
export { configureLayout };
